'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  getPostComments,
  addPostComment,
  getCommunityPost,
  toggleFollowUser,
} from '@/lib/api-client';
import Spinner from './Spinner';
import TagChip from './TagChip';
import PostKebabMenu from './PostKebabMenu';

interface FeedPost {
  id: string;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  contentType: 'image' | 'video';
  mediaUrl: string;
  thumbnailUrl: string | null;
  caption: string;
  tags: string[];
  likesCount: number;
  commentsCount: number;
  liked: boolean;
  isFeatured: boolean;
  prompt: string | null;
  showPrompt: boolean;
  createdAt: string;
}

interface CommentData {
  id: string;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  text: string;
  replies: CommentData[];
  createdAt: string;
}

interface PostModalProps {
  post: FeedPost | null;
  currentUserId?: string;
  onClose: () => void;
  onLike: (postId: string) => void;
  onDelete: (postId: string) => void;
  onReport: (postId: string) => void;
  onCommentAdded: (postId: string) => void;
  canComment?: boolean;
}

function timeAgo(date: string) {
  const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return `${Math.floor(s / 604800)}w`;
}

function initials(name: string) {
  return name ? name.slice(0, 2).toUpperCase() : '?';
}

export default function PostModal({
  post,
  currentUserId,
  onClose,
  onLike,
  onDelete,
  onReport,
  onCommentAdded,
  canComment = true,
}: PostModalProps) {
  const router = useRouter();

  const [comments, setComments] = useState<CommentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [visible, setVisible] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);

  const open = post !== null;
  const isOwner = !!(post && currentUserId && post.userId === currentUserId);

  // Sync liked/likesCount from post prop
  useEffect(() => {
    if (post) {
      setLiked(post.liked);
      setLikesCount(post.likesCount);
    }
  }, [post?.liked, post?.likesCount]);

  // Mount/unmount with exit animation
  useEffect(() => {
    if (open) {
      setRendered(true);
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setVisible(false);
      const t = setTimeout(() => setRendered(false), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!rendered) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [rendered]);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Load comments + follow state whenever post changes
  useEffect(() => {
    if (!post) {
      setComments([]);
      setNextCursor(null);
      setIsFollowing(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setPostError(null);
    setCommentText('');
    setShowFullPrompt(false);

    Promise.all([
      getPostComments(post.id),
      getCommunityPost(post.id).catch(() => null),
    ]).then(([cRes, pRes]) => {
      if (cancelled) return;
      const cData = cRes.data || cRes;
      setComments(cData.comments || []);
      setNextCursor(cData.nextCursor || null);
      if (pRes) {
        const pData = pRes.data || pRes;
        if (pData.post) setIsFollowing(!!pData.post.isFollowing);
      }
    }).catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    return () => { cancelled = true; };
  }, [post?.id]);

  const loadMore = useCallback(async () => {
    if (!post || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await getPostComments(post.id, nextCursor);
      const data = res.data || res;
      setComments(prev => [...prev, ...(data.comments || [])]);
      setNextCursor(data.nextCursor || null);
    } catch {}
    finally { setLoadingMore(false); }
  }, [post?.id, nextCursor, loadingMore]);

  // Infinite scroll via IntersectionObserver rooted on scrollRef
  useEffect(() => {
    if (!sentinelRef.current || !nextCursor) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMore();
    }, { threshold: 0.1, root: scrollRef.current });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [nextCursor, loadMore]);

  const handleLike = () => {
    if (!post) return;
    setLiked(!liked);
    setLikesCount(liked ? likesCount - 1 : likesCount + 1);
    onLike(post.id);
  };

  const handleFollow = async () => {
    if (!post || followLoading) return;
    setFollowLoading(true);
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    try {
      const res = await toggleFollowUser(post.userId);
      const data = res.data || res;
      setIsFollowing(!!data.following);
    } catch {
      setIsFollowing(wasFollowing);
    } finally {
      setFollowLoading(false);
    }
  };

  const handlePost = async () => {
    if (!post || !commentText.trim() || submitting) return;
    setSubmitting(true);
    setPostError(null);
    try {
      const res = await addPostComment(post.id, commentText.trim());
      const c = (res.data || res).comment;
      setComments(prev => [...prev, { ...c, replies: [] }]);
      setCommentText('');
      onCommentAdded(post.id);
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    } catch (e: any) {
      setPostError(e?.message || 'Failed to post comment. Please try again.');
    }
    finally { setSubmitting(false); }
  };

  const handleCopyLink = () => {
    if (!post || typeof window === 'undefined') return;
    const url = `${window.location.origin}/dashboard/community/post/${post.id}`;
    navigator.clipboard.writeText(url).catch(() => {});
  };

  const handleFocusCommentInput = () => {
    commentInputRef.current?.focus();
  };

  const handleNavigate = (path: string) => {
    onClose();
    router.push(path);
  };

  if (!rendered || !post) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/85 lg:backdrop-blur-sm transition-opacity duration-300 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal — outer container is click-through except for the card itself */}
      <div
        className="fixed inset-0 z-[61] flex items-center justify-center p-0 lg:p-6 pointer-events-none"
        role="dialog"
        aria-modal="true"
        aria-label="Post"
      >
        <div
          className={`relative w-full h-full lg:w-[min(95vw,1200px)] lg:h-[min(92vh,860px)] bg-[#0d0d0d] lg:rounded-xl overflow-hidden flex flex-col lg:flex-row shadow-2xl pointer-events-auto transform transition-all duration-300 ease-out ${
            visible ? 'opacity-100 lg:scale-100' : 'opacity-0 lg:scale-95'
          }`}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors lg:top-4 lg:right-4"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* MEDIA — left on desktop, top on mobile. A blurred cover-fill of the same media
              sits behind the contained image, so portrait/landscape posts read cleaner than
              hard black bars. */}
          <div className="relative flex-shrink-0 overflow-hidden bg-black flex items-center justify-center w-full lg:flex-1 lg:min-w-0 max-h-[50vh] lg:max-h-none">
            {(post.contentType === 'video' ? post.thumbnailUrl : post.mediaUrl) && (
              <img
                src={(post.contentType === 'video' ? post.thumbnailUrl : post.mediaUrl) || undefined}
                alt=""
                aria-hidden
                className="pointer-events-none absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-40"
              />
            )}
            {post.contentType === 'video' ? (
              <video
                src={post.mediaUrl}
                controls
                autoPlay
                loop
                muted
                playsInline
                className="relative max-w-full max-h-full object-contain"
              />
            ) : (
              <img
                src={post.mediaUrl}
                alt=""
                className="relative max-w-full max-h-full object-contain"
              />
            )}
          </div>

          {/* SIDEBAR — right on desktop, bottom on mobile */}
          <div className="flex flex-col flex-1 lg:flex-initial lg:w-[420px] lg:min-w-[380px] min-h-0 border-t lg:border-t-0 lg:border-l border-white/[0.08]">
            {/* Header */}
            <header className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] flex-shrink-0 pr-14 lg:pr-16">
              <button
                onClick={() => handleNavigate(`/dashboard/community/profile/${post.userId}`)}
                className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0 flex-1"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 p-[2px] flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-[#0d0d0d] flex items-center justify-center text-xs text-white/70 font-bold overflow-hidden">
                    {post.avatarUrl
                      ? <img src={post.avatarUrl} alt="" className="w-full h-full object-cover" />
                      : initials(post.displayName)}
                  </div>
                </div>
                <div className="text-left min-w-0">
                  <div className="text-white text-sm font-semibold leading-tight truncate">
                    {post.displayName}
                  </div>
                  <div className="text-white/30 text-[11px]">{timeAgo(post.createdAt)}</div>
                </div>
              </button>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isOwner && currentUserId && (
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-all ${
                      isFollowing
                        ? 'bg-white/[0.06] text-white/60 hover:bg-white/[0.1]'
                        : 'bg-purple-500 text-white hover:bg-purple-400'
                    } disabled:opacity-60`}
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                )}
                <PostKebabMenu
                  isOwner={isOwner}
                  onCopyLink={handleCopyLink}
                  onReport={() => onReport(post.id)}
                  onDelete={() => onDelete(post.id)}
                />
              </div>
            </header>

            {/* Scrollable body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain min-h-0">
              {/* Caption + tags + prompt block */}
              {(post.caption || post.tags.length > 0 || post.prompt) && (
                <div className="px-4 py-3 border-b border-white/[0.04]">
                  {post.caption && (
                    <div className="flex gap-2.5">
                      <button
                        onClick={() => handleNavigate(`/dashboard/community/profile/${post.userId}`)}
                        className="flex-shrink-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 p-[1.5px]">
                          <div className="w-full h-full rounded-full bg-[#0d0d0d] flex items-center justify-center text-[10px] text-white/70 font-bold overflow-hidden">
                            {post.avatarUrl
                              ? <img src={post.avatarUrl} alt="" className="w-full h-full object-cover" />
                              : initials(post.displayName)}
                          </div>
                        </div>
                      </button>
                      <div className="min-w-0 flex-1">
                        <span className="text-white text-xs font-semibold mr-1.5">
                          {post.displayName}
                        </span>
                        <span className="text-white/80 text-xs break-words">{post.caption}</span>
                      </div>
                    </div>
                  )}

                  {post.tags.length > 0 && (
                    <div className={`flex flex-wrap gap-1 mt-2 ${post.caption ? 'ml-[42px]' : ''}`}>
                      {post.tags.map(tag => <TagChip key={tag} label={tag} navigate />)}
                    </div>
                  )}

                  {post.prompt && (
                    <div className={`mt-3 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 ${post.caption ? 'ml-[42px]' : ''}`}>
                      <div className="text-white/30 text-[10px] uppercase tracking-wider mb-1">Prompt</div>
                      <div className={`text-white/50 text-xs leading-relaxed ${showFullPrompt ? '' : 'line-clamp-3'}`}>
                        {post.prompt}
                      </div>
                      {post.prompt.length > 140 && (
                        <button
                          onClick={() => setShowFullPrompt(!showFullPrompt)}
                          className="text-purple-400 hover:text-purple-300 text-[11px] mt-1 transition-colors"
                        >
                          {showFullPrompt ? 'Show less' : 'Show full prompt'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Comments */}
              {loading ? (
                <div className="flex justify-center py-10">
                  <Spinner size={28} className="text-white/50" />
                </div>
              ) : comments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-white/[0.04] flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-white/50 text-sm font-medium">No comments yet</p>
                  <p className="text-white/25 text-xs mt-1">Be the first to share your thoughts</p>
                </div>
              ) : (
                <div className="px-4 py-3 space-y-3">
                  {comments.map(c => (
                    <div key={c.id}>
                      <div className="flex gap-2.5">
                        <button
                          onClick={() => handleNavigate(`/dashboard/community/profile/${c.userId}`)}
                          className="flex-shrink-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] text-white/50 font-bold overflow-hidden">
                            {c.avatarUrl
                              ? <img src={c.avatarUrl} alt="" className="w-full h-full object-cover" />
                              : initials(c.displayName)}
                          </div>
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <button
                              onClick={() => handleNavigate(`/dashboard/community/profile/${c.userId}`)}
                              className="text-white text-xs font-semibold hover:underline"
                            >
                              {c.displayName}
                            </button>
                            <span className="text-white/25 text-[10px]">{timeAgo(c.createdAt)}</span>
                          </div>
                          <p className="text-white/75 text-xs mt-0.5 break-words leading-relaxed">{c.text}</p>
                        </div>
                      </div>
                      {c.replies.length > 0 && (
                        <div className="ml-10 mt-2 space-y-2">
                          {c.replies.map(r => (
                            <div key={r.id} className="flex gap-2">
                              <div className="w-6 h-6 rounded-full bg-white/[0.04] flex items-center justify-center text-[9px] text-white/40 font-bold flex-shrink-0 overflow-hidden">
                                {r.avatarUrl
                                  ? <img src={r.avatarUrl} alt="" className="w-full h-full object-cover" />
                                  : initials(r.displayName)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-2">
                                  <span className="text-white/80 text-[11px] font-semibold">{r.displayName}</span>
                                  <span className="text-white/20 text-[9px]">{timeAgo(r.createdAt)}</span>
                                </div>
                                <p className="text-white/60 text-[11px] mt-0.5 break-words leading-relaxed">{r.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {nextCursor && (
                    <div ref={sentinelRef} className="flex justify-center py-4">
                      {loadingMore && <Spinner size={20} className="text-white/50" />}
                    </div>
                  )}
                  {!nextCursor && comments.length > 6 && (
                    <p className="text-center text-white/15 text-[11px] py-3">· End of comments ·</p>
                  )}
                </div>
              )}
            </div>

            {/* Action buttons + input footer */}
            <footer className="flex-shrink-0 border-t border-white/[0.08] bg-[#0d0d0d]">
              {/* Actions row */}
              <div className="px-4 pt-3 pb-2">
                <div className="flex items-center gap-4 mb-1.5">
                  <button
                    onClick={handleLike}
                    aria-label={liked ? 'Unlike' : 'Like'}
                    className="transition-transform active:scale-125"
                  >
                    <svg
                      className={`w-6 h-6 ${liked ? 'text-red-500 fill-red-500' : 'text-white hover:text-white/70'}`}
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={liked ? 0 : 2}
                      fill={liked ? 'currentColor' : 'none'}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={handleFocusCommentInput}
                    aria-label="Write a comment"
                    className="text-white hover:text-white/70 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                  <button
                    onClick={handleCopyLink}
                    aria-label="Copy link"
                    className="text-white hover:text-white/70 transition-colors ml-auto"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </button>
                </div>
                <div className="text-white text-sm font-semibold">
                  {likesCount.toLocaleString()} {likesCount === 1 ? 'like' : 'likes'}
                </div>
              </div>

              {/* Comment input */}
              {canComment ? (
                <div className="border-t border-white/[0.06] px-4 py-3">
                  <div className="flex gap-2 items-center">
                    <input
                      ref={commentInputRef}
                      value={commentText}
                      onChange={e => {
                        setCommentText(e.target.value);
                        if (postError) setPostError(null);
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') handlePost(); }}
                      placeholder="Add a comment..."
                      aria-label="Add a comment"
                      aria-invalid={!!postError}
                      aria-describedby={postError ? 'post-modal-comment-error' : undefined}
                      className={`flex-1 bg-white/[0.04] border rounded-full px-4 py-2 text-white text-sm placeholder:text-white/25 outline-none transition-colors ${
                        postError
                          ? 'border-red-500/40 focus:border-red-500/60'
                          : 'border-white/[0.08] focus:border-purple-500/40'
                      }`}
                      maxLength={500}
                    />
                    <button
                      onClick={handlePost}
                      disabled={!commentText.trim() || submitting}
                      className="text-purple-400 hover:text-purple-300 disabled:text-white/15 text-sm font-semibold transition-colors px-1 disabled:cursor-not-allowed"
                    >
                      {submitting ? '...' : 'Post'}
                    </button>
                  </div>
                  {postError && (
                    <p id="post-modal-comment-error" role="alert" className="text-red-400 text-[11px] mt-1.5 px-1 leading-snug">
                      {postError}
                    </p>
                  )}
                </div>
              ) : (
                <div className="border-t border-white/[0.06] px-4 py-3">
                  <p className="text-white/30 text-xs text-center">Sign in to comment</p>
                </div>
              )}
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}
